import { getAlbumFirstAndLastSongs, putPlayItem, putRepeatMode } from "../controllers/SpotifyApiController";
import { getPointedDeviceId } from "../reducers/devices";
import { getCurrentAlbum, getCurrentItem, getItemProgress } from "../reducers/playbackState";
import { getCurrentRandomAlbum, getCurrentRandomTrack, getPointedRandomAlbum, getQueue, getQueueLength, getQueuePointer, getRandomModeActive, getRandomRepeatMode, isRepeatModeOn} from "../reducers/randomMode";
import { getSavedAlbums } from "../reducers/albumListState";
import { getAutoNewRandomAlbum, getRandomMethod, getRandomQueueLimit } from "../reducers/userSettings";
import { getNewRandomAlbum } from "../utils/randomAlbumAlgorithms";
import { getPlayItemOffset, simplifyAlbum } from "../utils/spotifyUtils";
import { shuffleArray } from "../utils/utils";
import { setPlaybackAction } from "./disablingProcessesActions";
import { sortRecentlyPlayedAlbums } from "./albumListActions";
import { DEFAULT_RANDOM_REPEAT_MODE, DEFAULT_REPEAT_MODE, ITEM_TYPES, RANDOM_REPEAT_MODES, REPEAT_MODES } from "../utils/constants/enumConstants";
import { manageTooManyRequestsResponse } from "../utils/connectionUtils";

export const SET_RANDOM_MODE_ACTIVE = 'SET_RANDOM_MODE_ACTIVE';
export const SET_RANDOM_ALBUM_QUEUE = 'SET_RANDOM_ALBUM_QUEUE';
export const SET_POINTED_RANDOM_ALBUM = 'SET_POINTED_RANDOM_ALBUM';
export const SET_QUEUE_POINTER = 'SET_QUEUE_POINTER';
export const SET_RANDOM_CURRENT_TRACK = 'SET_RANDOM_CURRENT_TRACK';
export const SET_FIRST_SONG = 'SET_FIRST_SONG';
export const SET_LAST_SONG = 'SET_LAST_SONG';
export const SET_FINISHED_ALBUM = 'SET_FINISHED_ALBUM';
export const SET_RANDOM_REPEAT_MODE = 'SET_RANDOM_REPEAT_MODE';
export const SET_SHOWING_QUEUE_DISPLAYER = 'SET_SHOWING_QUEUE_DISPLAYER';
export const SET_EDITING_QUEUE = 'SET_EDITING_QUEUE';

export const setRandomModeActive = payload => ({ type: SET_RANDOM_MODE_ACTIVE, payload });
export const setRandomAlbumQueue = payload => ({ type: SET_RANDOM_ALBUM_QUEUE, payload });
export const setPointedRandomAlbum = payload => ({ type: SET_POINTED_RANDOM_ALBUM, payload });
export const setQueuePointer = payload => ({ type: SET_QUEUE_POINTER, payload });
export const setRandomCurrentTrack = track => ({ type: SET_RANDOM_CURRENT_TRACK, payload: { uri: track.uri, name: track.name } });
export const setFirstSong = payload => ({ type: SET_FIRST_SONG, payload });
export const setLastSong = payload => ({ type: SET_LAST_SONG, payload });
export const setFinishedAlbum = payload => ({ type: SET_FINISHED_ALBUM, payload });
export const setRandomRepeatMode = payload => ({ type: SET_RANDOM_REPEAT_MODE, payload });
export const setShowingQueueDisplayer = payload => ({ type: SET_SHOWING_QUEUE_DISPLAYER, payload });
export const setEditingQueue = payload => ({ type: SET_EDITING_QUEUE, payload });

// #region Random mode activation actions

export const startRandomMode = ({fromCurrentTrack = false}) => {
    const getAlbumToEnqueue = (currentAlbum, savedAlbums) => {
        // It there is no current album, nothing is returned
        if(!currentAlbum) return undefined;

        // It looks for the album into the saved albums
        const savedAlbum = savedAlbums.find(album => album.uri === currentAlbum.uri);

        // If it is saved, then it returns the saved album data
        if(savedAlbum) return savedAlbum;

        // In any other case, simplifies the whole Spotify album data
        const simplifiedAlbum = simplifyAlbum(currentAlbum);

        // And returns it
        return simplifiedAlbum;
    }

    return (dispatch, getState) => {
        const state = getState();

        dispatch(setRandomModeActive(true));
        // If the user wants to start the random mode considering the currently played album
        if(fromCurrentTrack)
        {
            // Firstly, it is checked if there is a paused random album that is the same as the one currently being played
            const currentAlbum = getCurrentAlbum(state);
            const currentRandomAlbum = getCurrentRandomAlbum(state);
            const currentTrack = getCurrentItem(state);
            const itemProgress = getItemProgress(state);
            if(currentRandomAlbum && currentAlbum.id === currentRandomAlbum.id)
            {
                // In that case, the current random album (the one paused) is pointed and played, considering the currently playing track and its progress
                dispatch(setCurrentRandomAlbumAsPointed({trackUri: currentTrack.uri, positionMs: itemProgress}));
                return;
            }
                
            // If not, secondly, it is checked if the pointed random album, i.e. the next to be played in the random queue, is the same as the one currently being played
            const pointedRandomAlbum = getPointedRandomAlbum(state);
            if(pointedRandomAlbum && currentAlbum.id === pointedRandomAlbum.id)
            {
                // In that case, that album in queue is played, considering the currently playing track and its progress
                dispatch(setNextRandomAlbumAsPointed({trackUri: currentTrack.uri, positionMs: itemProgress}));
                return;
            }

            // In any other case
            const savedAlbums = getSavedAlbums(state);
            // The currently played album has to be enqueue
            const albumToEnqueue = getAlbumToEnqueue(currentAlbum, savedAlbums);
            if(albumToEnqueue)
            {
                // It is enqueued before the current random album or before the next random album (depending if the random mode is paused or stopped)
                dispatch(enqueueAlbumAsPrevious(albumToEnqueue));
                // And then, it is pointed and played, considering also the currently playing track and its progress
                dispatch(setPreviousRandomAlbumAsPointed({trackUri: currentTrack.uri, positionMs: itemProgress}));
                return;
            }
        }

        // In any other case, the next random album is pointed and played from the beginning
        dispatch(setNextRandomAlbumAsPointed());
    }
}

export const resumeRandomMode = ({resumePausedAlbum = false}) => {
    return (dispatch, getState) => {
        const state = getState();
        const currentTrack = getCurrentItem(state);
        const currentRandomAlbum = getCurrentRandomAlbum(state);
        const currentRandomTrack = getCurrentRandomTrack(state);
        const itemProgress = getItemProgress(state);

        let trackUri = undefined;
        // If the user wants to resume the paused album and there is in fact a paused random album
        if(resumePausedAlbum && currentRandomAlbum && currentRandomTrack)
        {
            // The URI of the track where the random album was paused is saved
            trackUri = currentRandomTrack.uri;
        }

        let positionMs = 0;
        // If the user wants to resume the paused album, there is in fact a paused random album and also the currently played track is the same as the paused random album's paused track
        if(resumePausedAlbum && currentRandomAlbum && currentRandomTrack && currentRandomTrack.uri === currentTrack.uri)
        {
            // The progress of the currently played track is saved
            positionMs = itemProgress;
        }

        dispatch(setRandomModeActive(true));
        // The paused random album (the one that is the current random album) is pointed and played, telling in which track and progress has to be begin to be played
        dispatch(setCurrentRandomAlbumAsPointed({trackUri, positionMs}));
    }
}

export const stopRandomMode = () => {
    return dispatch => {
        dispatch(setRandomModeActive(false));
        dispatch(resetQueue());
    }
}

// #endregion Random mode activation actions

// #region Change queue pointer actions

export const initializePointedAlbum = () => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);

        if(queue && queue.length > 0)
        {
            dispatch(setPointedRandomAlbum(queue[0]));
        }
    }
}

export const setCurrentRandomAlbumAsPointed = ({trackUri = undefined, trackIndex = 0, positionMs = 0} = {}) => {
    return (dispatch, getState) => {
        const state = getState();
        const queuePointer = getQueuePointer(state);

        dispatch(setAlbumFromQueueAsPointed({queueIndex: queuePointer - 1, trackUri, trackIndex, positionMs}));
    }
}

export const setNextRandomAlbumAsPointed = ({trackUri = undefined, trackIndex = 0, positionMs = 0} = {}) => {
    return (dispatch, getState) => {
        const state = getState();
        const queueLength = getQueueLength(state);
        const queuePointer = getQueuePointer(state);
        const repeatModeOn = isRepeatModeOn(state);
        const autoNewRandomAlbum = getAutoNewRandomAlbum(state);

        let queueIndex = queuePointer;
        // If the current random album is the last of the queue
        if(queueLength === queuePointer)
        {
            // If the random repeat mode is on (set on queue or album)
            if(repeatModeOn)
            {
                // The next pointed album will be the first one of the queue
                queueIndex = 0;
            }
            // If the random repeat mode is off and the autoNewRandomAlbum setting is on
            else if(autoNewRandomAlbum)
            {
                // A new random album will be enqueue at the end of the queue and it will be the next played album
                dispatch(enqueueRandomAlbum());
            }
            // If the random repeat mode and thhe autoNewRandomAlbum setting are off
            else
            {
                // Nothing is done
                return;
            }
        }

        dispatch(setAlbumFromQueueAsPointed({queueIndex, trackUri, trackIndex, positionMs}));
    }
}

export const setPreviousRandomAlbumAsPointed = ({trackUri = undefined, trackIndex = 0, positionMs = 0} = {}) => {
    return (dispatch, getState) => {
        const state = getState();
        const queuePointer = getQueuePointer(state);
        const queueLength = getQueueLength(state);
        const repeatModeOn = isRepeatModeOn(state);

        let queueIndex = queuePointer - 2;
        if(queuePointer <= 1)
        {
            queueIndex = repeatModeOn ? queueLength - 1 : queuePointer - 1;
        }

        dispatch(setAlbumFromQueueAsPointed({queueIndex, trackUri, trackIndex, positionMs}));
    }
}

export const setAlbumFromQueueAsPointed = ({queueIndex, trackUri = undefined, trackIndex = 0, positionMs = 0, startRandomMode = false}) => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);
        const queueLength = getQueueLength(state);
        const queueLimit = getRandomQueueLimit(state);
        const autoNewRandomAlbum = getAutoNewRandomAlbum(state);
        const repeatModeOn = isRepeatModeOn(state);

        if(startRandomMode)
        {
            dispatch(setRandomModeActive(true));
        }

        let newQueuePointer = queueIndex + 1;

        // If there are no albums in queue or if the auto add setting is on, the random repeat mode is off and the end of the queue has been reached
        if(queueLength === 0 || (autoNewRandomAlbum && !repeatModeOn && queueIndex === queueLength - 1))
        {
            // Then a new random album is added in the end of the queue
            const reachedQueueLimit = dispatch(enqueueRandomAlbum(true));

            if(reachedQueueLimit)
            {
                newQueuePointer = queueLimit - 1;
            }
        }

        dispatch(setPointedRandomAlbum(
        {
            album: queue[queueIndex],
            trackUri,
            trackIndex,
            positionMs
        }));
        dispatch(setQueuePointer(newQueuePointer));
    }
}

// #endregion Change queue pointer actions

// #region Enqueue actions

export const enqueueAlbum = (album, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);

        const reachedQueueLimit = dispatch(addAlbumToQueueAt(album, queue.length, checkQueueLimit));

        return reachedQueueLimit;
    }
}

export const enqueueRandomAlbum = (checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);

        const reachedQueueLimit = dispatch(addRandomAlbumToQueueAt(queue.length, checkQueueLimit));

        return reachedQueueLimit;
    }
}

export const enqueueAlbumAsFirst = (album, playNow = false, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queuePointer = getQueuePointer(state);

        const reachedQueueLimit = dispatch(addAlbumToQueueAt(album, queuePointer, checkQueueLimit));

        if(playNow)
        {
            dispatch(setNextRandomAlbumAsPointed());
        }

        return reachedQueueLimit;
    }
}

export const enqueueRandomAlbumAsFirst = (playNow = false, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queuePointer = getQueuePointer(state);

        const reachedQueueLimit = dispatch(addRandomAlbumToQueueAt(queuePointer, checkQueueLimit));

        if(playNow)
        {
            dispatch(setNextRandomAlbumAsPointed());
        }

        return reachedQueueLimit;
    }
}

export const enqueueAlbumAsPrevious = (album, playNow = false, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queuePointer = getQueuePointer(state);

        const reachedQueueLimit = dispatch(addAlbumToQueueAt(album, queuePointer - 1, checkQueueLimit));

        if(playNow)
        {
            dispatch(setCurrentRandomAlbumAsPointed());
        }

        return reachedQueueLimit;
    }
}

export const addAlbumToQueueAt = (album, queueIndex, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);
        const queueLimit = getRandomQueueLimit(state);

        let newQueue;
        if(queueIndex >= queue.length) {
            newQueue = [...queue, album];            
        }
        else if(queueIndex >= 0) {
            const queueFirstHalf = queue.slice(0, queueIndex);
            const queueSecondHalf = queue.slice(queueIndex, queue.length);

            newQueue = [...queueFirstHalf, album, ...queueSecondHalf];
        }
        else {
            newQueue = [album, ...queue];
        }

        album.inQueue = true;

        const queuePointer = getQueuePointer(state);
        let newQueuePointer = queuePointer;

        // The new album is being included in the queue in a position previous to the one that queuePointer is pointing
        if(queueIndex < queuePointer)
        {
            // Then, the queuePointer has to point to the next position, since the album it was pointing has been moved one position forward
            newQueuePointer += 1;
        }

        let reachedQueueLimit = false;

        if(checkQueueLimit && newQueue.length > queueLimit)
        {
            let dequeuedAlbums = 0;
            for(let i = 0; i < newQueue.length - queueLimit; i++)
            {
                dequeuedAlbums++;
                newQueue[i].inQueue = false;
            }
            newQueue = newQueue.slice(newQueue.length - queueLimit, newQueue.length);

            const randomModeActive = getRandomModeActive(state);
            newQueuePointer -= dequeuedAlbums;

            if(randomModeActive)
            {
                if(newQueuePointer < 1)
                {
                    newQueuePointer = 1;
                }
            }
            else if(newQueuePointer < 0)
            {
                newQueuePointer = 0;
            }
            
            reachedQueueLimit = true;
        }
        
        dispatch(setRandomAlbumQueue(newQueue));
        dispatch(setQueuePointer(newQueuePointer));

        return reachedQueueLimit;
    }
}

export const addRandomAlbumToQueueAt = (queueIndex, checkQueueLimit = false) => {
    return (dispatch, getState) => {
        const state = getState();
        const randomMethod = getRandomMethod(state);
        const savedAlbums = getSavedAlbums(state);
        const newAlbum = getNewRandomAlbum(savedAlbums, randomMethod, true);
        newAlbum.inQueue = true;

        const reachedQueueLimit = dispatch(addAlbumToQueueAt(newAlbum, queueIndex, checkQueueLimit));

        return reachedQueueLimit;
    }
}

// #endregion Enqueue actions

// #region Other actions

export const playPointedRandomAlbum = () => {
    return (dispatch, getState) => {
        const state = getState();
        const albumData = getPointedRandomAlbum(state);
        const pointedDeviceId = getPointedDeviceId(state);

        dispatch(setPlaybackAction(true));

        getAlbumFirstAndLastSongs(albumData.album.id)
            .then((result) => {
                const { firstSong, lastSong } = result;
                dispatch(setFirstSong(firstSong));
                dispatch(setLastSong(lastSong));
                dispatch(setFinishedAlbum(false));

                const offset = getPlayItemOffset(albumData.trackUri ? albumData.trackUri : albumData.trackIndex);

                putPlayItem(albumData.album.id, ITEM_TYPES.album, pointedDeviceId, offset, albumData.positionMs)
                    .then(() => {
                        dispatch(sortRecentlyPlayedAlbums(albumData.album));
                        dispatch(setPlaybackAction(false));
                    })
                    .catch((error) => {
                        dispatch(setPlaybackAction(false));
                        manageTooManyRequestsResponse(error, dispatch);
                        console.error(`Error playing item ${albumData.album.id}: `, error);
                    })
            })
            .catch((error) => {
                dispatch(setPlaybackAction(false));
                manageTooManyRequestsResponse(error, dispatch);
                console.error(`Error getting album ${albumData.album.id} last song: `, error);
            });
    }
}

export const rerandomizeAlbumFromQueue = (oldAlbum, queueIndex) => {
    return (dispatch, getState) => {
        const state = getState();
        const randomMethod = getRandomMethod(state);
        const savedAlbums = getSavedAlbums(state);

        const queue = getQueue(state);
        const queuePointer = getQueuePointer(state);
        const newAlbum = getNewRandomAlbum(savedAlbums, randomMethod);

        oldAlbum.inQueue = false;
        newAlbum.inQueue = true;

        const queueFirstHalf = queue.slice(0, queueIndex);
        const queueSecondHalf = queue.slice(queueIndex + 1, queue.length);

        dispatch(setRandomAlbumQueue([...queueFirstHalf, newAlbum, ...queueSecondHalf]));

        // If the current random album is the one to be rerandomized from the queue
        if(queueIndex === queuePointer - 1)
        {
            // It has to be played again
            dispatch(setCurrentRandomAlbumAsPointed());
        }
    }
}

export const shuffleQueue = () => {
    const shuffleStartedQueue = (queue, queuePointer) => {
        const queueLength = queue.length;
        const currentAlbumIndex = queuePointer - 1;
        const currentAlbum = queue[currentAlbumIndex];

        let firstHalfQueue = queue.slice(0, currentAlbumIndex);
        let secondHalfQueue = queue.slice(currentAlbumIndex + 1, queueLength);

        const queueWithoutCurrent = [...firstHalfQueue, ...secondHalfQueue];
        const shuffledQueueWithoutCurrent = shuffleArray(queueWithoutCurrent);

        firstHalfQueue = shuffledQueueWithoutCurrent.slice(0, currentAlbumIndex);
        secondHalfQueue = shuffledQueueWithoutCurrent.slice(currentAlbumIndex, queueLength - 1);

        const shuffledQueue = [...firstHalfQueue, currentAlbum, ...secondHalfQueue];

        return shuffledQueue;
    }

    const shuffleStoppedQueue = (queue) => {
        const shuffledQueue = shuffleArray(queue);

        return shuffledQueue;
    }

    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);
        const queuePointer = getQueuePointer(state);

        if(queuePointer > 0)
        {
            const shuffledQueue = shuffleStartedQueue(queue, queuePointer);

            dispatch(setRandomAlbumQueue(shuffledQueue));
        }
        else
        {
            const shuffledQueue = shuffleStoppedQueue(queue);

            dispatch(setRandomAlbumQueue(shuffledQueue));
        }
    }
}

export const toggleRandomRepeatMode = () => {
    return (dispatch, getState) => {
        const setMode = (randomRepeatMode, spotifyRepeatMode) => {
            putRepeatMode(spotifyRepeatMode, pointedDeviceId)
                .then(() => {
                    dispatch(setRandomRepeatMode(randomRepeatMode));
                })
                .catch((error) => { 
                    manageTooManyRequestsResponse(error, dispatch);
                    console.error('Error changing repeat mode: ', error)
                });
        }

        const state = getState();
        const randomRepeatMode = getRandomRepeatMode(state);
        const pointedDeviceId = getPointedDeviceId(state);

        switch(randomRepeatMode) {
            case RANDOM_REPEAT_MODES.off:
                setMode(RANDOM_REPEAT_MODES.queue, REPEAT_MODES.off);
                break;
            case RANDOM_REPEAT_MODES.queue:
                setMode(RANDOM_REPEAT_MODES.album, REPEAT_MODES.context);
                break;
            case RANDOM_REPEAT_MODES.album:
                setMode(RANDOM_REPEAT_MODES.off, REPEAT_MODES.off);
                break;
            default:
                setMode(DEFAULT_RANDOM_REPEAT_MODE, DEFAULT_REPEAT_MODE);
        }
    }
}

export const deleteAlbumFromQueue = (album, queueIndex) => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);
        const queuePointer = getQueuePointer(state);

        album.inQueue = false;

        const queueFirstHalf = queue.slice(0, queueIndex);
        const queueSecondHalf = queue.slice(queueIndex + 1, queue.length);

        dispatch(setRandomAlbumQueue([...queueFirstHalf, ...queueSecondHalf]));

        // If the current random album is the one to be deleted from the queue and there are at least two albums in the queue
        if(queueIndex === queuePointer - 1 && queue.length > 1)
        {
            // If the current random album is not the last one of the queue
            if(queueIndex < queue.length - 1)
            {
                // The next album to be played, since the current random album has been deleted, will be the next random album
                dispatch(setCurrentRandomAlbumAsPointed());
            }
            // If the current album is the last one of the queue
            else
            {
                // The next album to be played will be the previous album
                dispatch(setPreviousRandomAlbumAsPointed());
            }
        }
        else if(queue.length === 1) {
            dispatch(setRandomModeActive(false));
        }

        if(queueIndex < queuePointer - 1)
        {
            dispatch(setQueuePointer(queuePointer - 1));
        }
    }
}

export const resetQueue = () => {
    return (dispatch, getState) => {
        const state = getState();
        const queue = getQueue(state);

        queue.forEach((album) => album.inQueue = false);

        dispatch(setRandomAlbumQueue([]));
        dispatch(setQueuePointer(0));

        dispatch(enqueueRandomAlbum());
    }
}

// #endregion Other actions